import * as React from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useColorModeValue } from "@chakra-ui/react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardAction, CardHeader } from "@/components/ui/card";
import { CopyIcon, type CopyIconHandle } from "@/components/ui/copy-icon";
import { FieldDescription, FieldLabel } from "@/components/ui/field";
import { TrashIcon, type TrashIconHandle } from "@/components/ui/trash";
import { cn } from "@/lib/utils";
import { PineScriptCodeBlock } from "../../../components/Reports/PineScriptCodeBlock";
import {
  COMPOSE_DIALOG_DESCRIPTION,
  STRATEGY_COMPOSE_DIALOG_CONTENT_CLASS,
  StrategyComposeModal,
  type EmbeddedComposePresentation,
} from "../../../components/Strategies/StrategyComposeModal";
import { StrategyPineDetailDialog } from "../../../components/Strategies/StrategyPineDetailDialog";
import { DeleteActivePinescriptDialog } from "../../../components/Strategies/DeleteActivePinescriptDialog";
import {
  STRATEGY_PINE_SECTION_DESC_CLASS,
  STRATEGY_PINE_SECTION_LABEL_CLASS,
} from "../../../components/Strategies/StrategyPineSectionCard";
import {
  deleteReportStrategyComposedPinescript,
  ReportsApiError,
} from "../../../services/reportsApi";
import { toastError } from "../../../utils/showApiErrorToast";
import { EditStrategyDialog } from "../Strategies/EditStrategyDialog";
import { REPORT_DETAIL_CARD_BORDER } from "../Reports/components/ReportsCardChrome";
import type { WorkspaceStrategyPineState } from "./useWorkspaceStrategyPine";
import type { WorkspacePolicyPinePreviewState } from "./useWorkspacePolicyPinePreview";
import { WORKSPACE_PINE_DOCK_PANEL_GAP_PX } from "./workspaceDrawerWidth";
import {
  dispatchWorkspacePineActiveRemoved,
  dispatchWorkspacePineComposeFailed,
  dispatchWorkspacePineComposeRefresh,
  dispatchWorkspacePineComposeStart,
} from "./workspacePineComposeEvents";
import { WORKSPACE_OVERLAY_TRANSITION } from "./workspaceOverlayMotion";
import { WorkspaceDrawerCloseButton } from "./WorkspaceDrawerActionRow";

const MASTER_PINE_PANEL_WIDTH = `calc((100% - ${WORKSPACE_PINE_DOCK_PANEL_GAP_PX}px) / 2)`;
const MASTER_PINE_RIGHT_SLOT_LEFT = `calc(50% + ${WORKSPACE_PINE_DOCK_PANEL_GAP_PX / 2}px)`;

export const WORKSPACE_PINE_DOCK_HEIGHT_PX = 280;

export type WorkspacePineDockProps = {
  pine: WorkspaceStrategyPineState;
};

async function copyText(label: string, text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  } catch {
    toast.error(`Failed to copy ${label.toLowerCase()}`);
  }
}

function downloadText(text: string, filename: string): void {
  if (!text.trim()) return;
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function PineScriptCopyIconButton({
  disabled,
  ariaLabel,
  onClick,
}: {
  disabled: boolean;
  ariaLabel: string;
  onClick: () => void;
}): React.ReactElement {
  const copyIconRef = useRef<CopyIconHandle | null>(null);

  const startCopyIconAnimation = useCallback(() => {
    void copyIconRef.current?.startAnimation();
  }, []);

  const stopCopyIconAnimation = useCallback(() => {
    void copyIconRef.current?.stopAnimation();
  }, []);

  const copyIconHoverHandlers = useMemo(
    () => ({
      onPointerDownCapture: startCopyIconAnimation,
      onMouseEnter: startCopyIconAnimation,
      onMouseLeave: stopCopyIconAnimation,
    }),
    [startCopyIconAnimation, stopCopyIconAnimation]
  );

  return (
    <button
      type="button"
      data-pine-script-copy=""
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      {...copyIconHoverHandlers}
    >
      <CopyIcon ref={copyIconRef} size={16} className="shrink-0 text-current" aria-hidden />
    </button>
  );
}

function PineScriptDeleteIconButton({
  disabled,
  ariaLabel,
  onClick,
}: {
  disabled: boolean;
  ariaLabel: string;
  onClick: () => void;
}): React.ReactElement {
  const deleteIconRef = useRef<TrashIconHandle | null>(null);

  const startDeleteIconAnimation = useCallback(() => {
    void deleteIconRef.current?.startAnimation();
  }, []);

  const stopDeleteIconAnimation = useCallback(() => {
    void deleteIconRef.current?.stopAnimation();
  }, []);

  const deleteIconHoverHandlers = useMemo(
    () => ({
      onPointerDownCapture: startDeleteIconAnimation,
      onMouseEnter: startDeleteIconAnimation,
      onMouseLeave: stopDeleteIconAnimation,
    }),
    [startDeleteIconAnimation, stopDeleteIconAnimation]
  );

  return (
    <button
      type="button"
      data-pine-script-delete=""
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      {...deleteIconHoverHandlers}
    >
      <TrashIcon ref={deleteIconRef} size={16} className="shrink-0 text-current" aria-hidden />
    </button>
  );
}

function PineCodeBlockFrame({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden [&_[data-pine-script-code-block]]:flex [&_[data-pine-script-code-block]]:h-full [&_[data-pine-script-code-block]]:min-h-0 [&_[data-pine-script-code-block]]:flex-1 [&_[data-pine-script-code-block]]:flex-col">
      {children}
    </div>
  );
}

function PanelBody({
  loading,
  error,
  emptyMessage,
  code,
  chromeTitle,
  chromeSubtitle,
  pineCardKind,
  composeSessionActive,
  composeSequence,
  onComposeRevealComplete,
}: {
  loading: boolean;
  error: string | null;
  emptyMessage: string | null;
  code: string;
  chromeTitle: string;
  chromeSubtitle: string;
  pineCardKind: "master" | "active" | "policy";
  composeSessionActive?: boolean;
  composeSequence?: number;
  onComposeRevealComplete?: () => void;
}): React.ReactElement {
  const isActivePanel = pineCardKind === "active";
  const codeTone = pineCardKind === "active" ? "active" : "master";
  const showActiveCodeShell = isActivePanel && (composeSessionActive || Boolean(code.trim()));

  if (showActiveCodeShell) {
    return (
      <PineCodeBlockFrame>
        <PineScriptCodeBlock
          code={code}
          maxH="100%"
          brandChrome
          tone="active"
          chromeTitle={chromeTitle}
          chromeSubtitle={composeSessionActive ? "Composed" : chromeSubtitle}
          scramble={
            composeSessionActive
              ? {
                  enabled: true,
                  waitingForCode: !code.trim(),
                  minScrambleMs: 5000,
                  onRevealComplete: onComposeRevealComplete,
                  sessionKey: composeSequence ?? 0,
                }
              : undefined
          }
        />
      </PineCodeBlockFrame>
    );
  }

  // Quiet placeholder while strategy pine loads — no spinner flash.
  if (loading) {
    return <div className="min-h-0 flex-1" aria-busy="true" aria-label="Loading Pinescript" />;
  }

  if (error) {
    return (
      <div className="flex flex-1 items-center px-3 py-4 font-[family-name:var(--font-primary)] text-sm text-destructive">
        {error}
      </div>
    );
  }

  if (!code.trim()) {
    return (
      <div className="flex flex-1 items-center px-3 py-4 font-[family-name:var(--font-primary)] text-sm text-muted-foreground dark:text-white">
        {emptyMessage ?? "Nothing to show yet."}
      </div>
    );
  }

  return (
    <PineCodeBlockFrame>
      <PineScriptCodeBlock
        code={code}
        maxH="100%"
        brandChrome
        tone={codeTone}
        chromeTitle={chromeTitle}
        chromeSubtitle={chromeSubtitle}
      />
    </PineCodeBlockFrame>
  );
}

function PinePanel({
  title,
  description,
  loading,
  error,
  emptyMessage,
  code,
  chromeTitle,
  chromeSubtitle,
  actions,
  pineCardKind,
  composeSessionActive,
  composeSequence,
  onComposeRevealComplete,
}: {
  title: string;
  description: string;
  loading: boolean;
  error: string | null;
  emptyMessage: string | null;
  code: string;
  chromeTitle: string;
  chromeSubtitle: string;
  actions: React.ReactNode;
  pineCardKind: "master" | "active" | "policy";
  composeSessionActive?: boolean;
  composeSequence?: number;
  onComposeRevealComplete?: () => void;
}): React.ReactElement {
  return (
    <div data-workspace-pine-panel="" data-pine-enter={pineCardKind} className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
      <Card
        data-slot="card"
        {...(pineCardKind === "active"
          ? { "data-strategy-active-pine-card": "" }
          : pineCardKind === "policy"
            ? { "data-strategy-policy-pine-card": "" }
            : { "data-strategy-master-pine-card": "" })}
        className="flex h-full min-h-0 flex-col gap-0 rounded-md border bg-card py-0 text-card-foreground shadow-none ring-0"
        style={{ borderColor: REPORT_DETAIL_CARD_BORDER }}
      >
      <CardHeader className="grid-cols-[1fr_auto] shrink-0 items-start gap-3 px-3 py-3 has-data-[slot=card-action]:grid-cols-[1fr_auto]">
        <div className="flex min-w-0 flex-col gap-1 text-left">
          <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>{title}</FieldLabel>
          <FieldDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
            {description}
          </FieldDescription>
        </div>
        <CardAction className="col-start-2 row-start-1 justify-self-end">
          <div data-pine-script-actions="" className="flex flex-wrap items-center justify-end gap-2">
            {actions}
          </div>
        </CardAction>
      </CardHeader>
      <div className="flex min-h-0 flex-1 flex-col px-3 pb-3">
        <PanelBody
          loading={loading}
          error={error}
          emptyMessage={emptyMessage}
          code={code}
          chromeTitle={chromeTitle}
          chromeSubtitle={chromeSubtitle}
          pineCardKind={pineCardKind}
          composeSessionActive={composeSessionActive}
          composeSequence={composeSequence}
          onComposeRevealComplete={onComposeRevealComplete}
        />
      </div>
      </Card>
    </div>
  );
}

function activePineDescription(pine: WorkspaceStrategyPineState): string {
  if (pine.activeStale) {
    return "Master Pinescript changed. Re-compose to refresh your TradingView script.";
  }
  if (pine.activeCode.trim() || pine.activeIndicatorCode.trim()) {
    return "Composed TradingView export for this strategy.";
  }
  return "Compose a TradingView Pinescript from a saved policy in the strategy drawer.";
}

function PinePanelHideButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}): React.ReactElement {
  return <WorkspaceDrawerCloseButton onClose={onClick} aria-label={label} />;
}

function MasterPineCardActions({
  pine,
}: {
  pine: WorkspaceStrategyPineState;
}): React.ReactElement {
  const strategyId = pine.strategyId ?? "";
  const masterCode = pine.masterCode;
  const canUseMasterCode = Boolean(masterCode.trim()) && !pine.loading;
  const [editOpen, setEditOpen] = useState(false);

  return (
    <>
      <PineScriptCopyIconButton
        disabled={!canUseMasterCode}
        ariaLabel="Copy Master Pinescript"
        onClick={() => void copyText("Master Pinescript", masterCode)}
      />
      <Button
        type="button"
        variant="outline-gray"
        size="sm"
        data-font="primary"
        data-pine-script-download=""
        className="shrink-0"
        disabled={!canUseMasterCode}
        onClick={() => downloadText(masterCode, `${strategyId}-master.pine`)}
      >
        <span className="normal-case tracking-normal">Download</span>
      </Button>
      <Button
        type="button"
        variant="outline-gray"
        size="sm"
        data-font="primary"
        data-strategy-master-pine-edit=""
        className="shrink-0"
        disabled={!strategyId || pine.loading}
        onClick={() => setEditOpen(true)}
      >
        <span className="normal-case tracking-normal">Edit</span>
      </Button>
      <PinePanelHideButton label="Hide Master Pinescript" onClick={pine.hideMaster} />

      <EditStrategyDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        strategyId={strategyId || undefined}
        listRow={pine.strategy}
        onSaved={async () => {
          setEditOpen(false);
          pine.refresh();
        }}
      />
    </>
  );
}

function ActivePineCardActions({
  pine,
}: {
  pine: WorkspaceStrategyPineState;
}): React.ReactElement {
  const strategyId = pine.strategyId ?? "";
  const activeCode = pine.activeCode || pine.activeIndicatorCode;
  const canUseActiveCode = Boolean(activeCode.trim()) && !pine.loading;
  const [deleting, setDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeFooter, setComposeFooter] = useState<React.ReactNode>(null);
  const [composePresentation, setComposePresentation] =
    useState<EmbeddedComposePresentation | null>(null);

  const textColor = useColorModeValue("gray.700", "gray.200");
  const subtleText = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  const handleDelete = useCallback(async () => {
    if (!strategyId || deleting) return;

    setDeleting(true);
    try {
      await deleteReportStrategyComposedPinescript(strategyId);
      toast.success("Composed Pinescript removed");
      setDeleteDialogOpen(false);
      dispatchWorkspacePineActiveRemoved(strategyId);
      dispatchWorkspacePineComposeRefresh(strategyId);
    } catch (e) {
      const description =
        e instanceof ReportsApiError || e instanceof Error
          ? e.message
          : "Could not remove composed Pinescript.";
      toastError("Remove failed", { description });
    } finally {
      setDeleting(false);
    }
  }, [deleting, pine, strategyId]);

  const openDeleteDialog = useCallback(() => {
    if (deleting || pine.loading || !strategyId) return;
    setDeleteDialogOpen(true);
  }, [deleting, pine.loading, strategyId]);

  const handleComposeClose = useCallback(() => {
    setComposeOpen(false);
    setComposeFooter(null);
    setComposePresentation(null);
  }, []);

  const handleComposeStart = useCallback(() => {
    if (!strategyId) return;
    handleComposeClose();
    dispatchWorkspacePineComposeStart(strategyId);
  }, [handleComposeClose, strategyId]);

  const handleComposeFailed = useCallback(() => {
    if (!strategyId) return;
    dispatchWorkspacePineComposeFailed(strategyId);
  }, [strategyId]);

  const handleComposeSuccess = useCallback(async () => {
    handleComposeClose();
    if (strategyId) {
      dispatchWorkspacePineComposeRefresh(strategyId);
    }
  }, [handleComposeClose, strategyId]);

  return (
    <>
      <PineScriptCopyIconButton
        disabled={!canUseActiveCode}
        ariaLabel="Copy Active Pinescript"
        onClick={() => void copyText("Active Pinescript", activeCode)}
      />
      <PineScriptDeleteIconButton
        disabled={deleting || pine.loading || !strategyId}
        ariaLabel="Remove composed Pinescript"
        onClick={openDeleteDialog}
      />
      <Button
        type="button"
        variant="outline-gray"
        size="sm"
        data-font="primary"
        data-pine-script-download=""
        className="shrink-0"
        disabled={!canUseActiveCode}
        onClick={() => downloadText(activeCode, `${strategyId}-composed.pine`)}
      >
        <span className="normal-case tracking-normal">Download</span>
      </Button>
      <Button
        type="button"
        variant="gradient"
        size="sm"
        data-font="primary"
        data-strategy-active-pine-recompose=""
        className="shrink-0"
        disabled={!pine.strategy || pine.loading}
        onClick={() => {
          setComposePresentation(null);
          setComposeFooter(null);
          setComposeOpen(true);
        }}
      >
        <span className="normal-case tracking-normal">Recompose</span>
      </Button>
      <PinePanelHideButton label="Hide Active Pinescript" onClick={pine.hideActive} />

      {pine.strategy ? (
        <StrategyPineDetailDialog
          open={composeOpen}
          onOpenChange={(open) => {
            if (!open) handleComposeClose();
            else setComposeOpen(true);
          }}
          size={composePresentation?.size ?? "compact"}
          contentClassName={STRATEGY_COMPOSE_DIALOG_CONTENT_CLASS}
          contentProps={{ "data-strategy-compose-dialog": "" }}
          title={composePresentation?.title ?? "Compose for TradingView"}
          description={composePresentation?.description ?? COMPOSE_DIALOG_DESCRIPTION}
          footer={composePresentation?.footer ?? composeFooter}
          showDismissButton={false}
        >
          {composeOpen ? (
            <StrategyComposeModal
              shell="embedded"
              isOpen
              onClose={handleComposeClose}
              onCancel={handleComposeClose}
              onEmbeddedPresentationChange={setComposePresentation}
              strategy={pine.strategy}
              versions={pine.composeVersions}
              currentPolicyVersionId={pine.currentPolicyVersionId}
              subtleText={subtleText}
              textColor={textColor}
              borderColor={borderColor}
              onStrategyRefresh={async () => {
                if (strategyId) {
                  dispatchWorkspacePineComposeRefresh(strategyId);
                }
              }}
              onComposeStart={handleComposeStart}
              onComposeFailed={handleComposeFailed}
              onComposeSuccess={handleComposeSuccess}
              onComposeFooterChange={setComposeFooter}
            />
          ) : null}
        </StrategyPineDetailDialog>
      ) : null}

      <DeleteActivePinescriptDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        isDeleting={deleting}
        onConfirm={() => void handleDelete()}
      />
    </>
  );
}

export function WorkspacePineDock({ pine }: WorkspacePineDockProps): React.ReactElement | null {
  const reduceMotion = useReducedMotion();
  const showMaster = pine.showMaster;
  const showActivePanel = pine.showActive || pine.composeSessionActive;

  const handleComposeRevealComplete = useCallback(() => {
    pine.endComposeSession();
  }, [pine]);

  if (!showMaster && !showActivePanel) {
    return null;
  }

  const panelCount = Number(showMaster) + Number(showActivePanel);
  const dualLayout = showMaster && showActivePanel;
  const masterInRightSlot = showMaster && !showActivePanel;
  const activeCode = pine.activeCode || pine.activeIndicatorCode;
  const slideTransition = reduceMotion ? { duration: 0 } : WORKSPACE_OVERLAY_TRANSITION;
  const activeExitTransition = reduceMotion
    ? { duration: 0 }
    : { ...WORKSPACE_OVERLAY_TRANSITION, duration: 0.24 };
  const keepMasterContentVisible =
    Boolean(pine.masterCode.trim()) || pine.composeSessionActive;

  const masterPanel = showMaster ? (
    <PinePanel
      pineCardKind="master"
      title="Master Pinescript"
      description="Your original TradingView script."
      loading={pine.loading && !keepMasterContentVisible}
      error={
        pine.loading && !keepMasterContentVisible
          ? null
          : pine.error && !pine.masterCode
            ? pine.error
            : null
      }
      emptyMessage={pine.loading && !keepMasterContentVisible ? null : pine.masterEmptyMessage}
      code={pine.masterCode}
      chromeTitle="Master"
      chromeSubtitle="Source"
      actions={<MasterPineCardActions pine={pine} />}
    />
  ) : null;

  const activePanel = showActivePanel ? (
    <PinePanel
      pineCardKind="active"
      title="Active Pinescript"
      description={
        pine.composeSessionActive
          ? "Generating your TradingView Pinescript."
          : pine.loading
            ? "Compose a TradingView Pinescript from a saved policy in the strategy drawer."
            : activePineDescription(pine)
      }
      loading={pine.loading && !pine.composeSessionActive}
      error={
        pine.composeSessionActive
          ? null
          : pine.loading
            ? null
            : pine.error && !activeCode
              ? pine.error
              : null
      }
      emptyMessage={
        pine.composeSessionActive ? null : pine.loading ? null : pine.activeEmptyMessage
      }
      code={pine.composeSessionActive ? activeCode : pine.loading ? "" : activeCode}
      chromeTitle="Active"
      chromeSubtitle={
        pine.composeSessionActive
          ? "Composing…"
          : pine.loading
            ? "Composed"
            : pine.activeStale
              ? "Outdated"
              : "Composed"
      }
              actions={
                pine.composeSessionActive ? (
                  <div className="invisible flex flex-wrap items-center justify-end gap-2 pointer-events-none" aria-hidden>
                    <ActivePineCardActions pine={pine} />
                  </div>
                ) : (
                  <ActivePineCardActions pine={pine} />
                )
              }
      composeSessionActive={pine.composeSessionActive}
      composeSequence={pine.composeSequence}
      onComposeRevealComplete={handleComposeRevealComplete}
    />
  ) : null;

  return (
    <div
      data-workspace-pine-dock=""
      data-workspace-pine-dock-panel-count={panelCount}
      className="pointer-events-auto relative h-full min-h-0 w-full overflow-hidden"
      style={{ height: WORKSPACE_PINE_DOCK_HEIGHT_PX }}
    >
      {showMaster ? (
        <motion.div
          data-workspace-pine-dock-slot=""
          data-workspace-pine-dock-slot-position={masterInRightSlot ? "right" : "left"}
          className={cn(
            "absolute inset-y-0 flex min-h-0 min-w-0 flex-col overflow-hidden",
            !dualLayout && "z-10"
          )}
          style={{ width: MASTER_PINE_PANEL_WIDTH }}
          initial={false}
          animate={{ left: masterInRightSlot ? MASTER_PINE_RIGHT_SLOT_LEFT : 0 }}
          transition={slideTransition}
        >
          {masterPanel}
        </motion.div>
      ) : null}

      <AnimatePresence initial={false}>
        {showActivePanel ? (
          <motion.div
            key="workspace-active-pine-panel"
            data-workspace-pine-panel=""
            data-workspace-pine-dock-slot=""
            data-workspace-pine-dock-slot-position="right"
            data-pine-enter="active"
            className="absolute inset-y-0 right-0 z-0 flex min-h-0 min-w-0 flex-col overflow-hidden"
            style={{ width: MASTER_PINE_PANEL_WIDTH }}
            initial={reduceMotion ? false : { opacity: 0, x: 28 }}
            animate={
              reduceMotion
                ? { opacity: 1, x: 0 }
                : { opacity: 1, x: 0, transition: slideTransition }
            }
            exit={
              reduceMotion
                ? undefined
                : { opacity: 0, x: 40, transition: activeExitTransition }
            }
          >
            {activePanel}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

export type WorkspacePolicyPinePreviewDockProps = {
  preview: WorkspacePolicyPinePreviewState;
};

/** Half-width left dock card for a policy version's Pinescript (opened from the policy sheet). */
export function WorkspacePolicyPinePreviewDock({
  preview,
}: WorkspacePolicyPinePreviewDockProps): React.ReactElement | null {
  if (!preview.open) return null;

  const canUseCode = Boolean(preview.code.trim()) && !preview.loading;
  const filenameBase =
    preview.policyVersionId?.replace(/[^\w.-]+/g, "-").slice(0, 48) || "policy";

  return (
    <div
      data-workspace-pine-dock=""
      data-workspace-pine-dock-panel-count={1}
      data-workspace-policy-pine-preview=""
      className="pointer-events-auto relative h-full min-h-0 w-full overflow-hidden"
      style={{ height: WORKSPACE_PINE_DOCK_HEIGHT_PX }}
    >
      <div
        data-workspace-pine-dock-slot=""
        data-workspace-pine-dock-slot-position="left"
        className="absolute inset-y-0 left-0 flex min-h-0 min-w-0 flex-col overflow-hidden"
        style={{ width: MASTER_PINE_PANEL_WIDTH }}
      >
        <PinePanel
          pineCardKind="policy"
          title="Policy Pinescript"
          description={
            preview.loading
              ? "Loading Pinescript for this policy version…"
              : `Generated Pinescript for ${preview.title}.`
          }
          loading={preview.loading}
          error={preview.error}
          emptyMessage={preview.loading ? null : "No Pinescript available for this version."}
          code={preview.code}
          chromeTitle="Policy"
          chromeSubtitle={preview.title}
          actions={
            <>
              <PineScriptCopyIconButton
                disabled={!canUseCode}
                ariaLabel="Copy Policy Pinescript"
                onClick={() => void copyText("Policy Pinescript", preview.code)}
              />
              <Button
                type="button"
                variant="outline-gray"
                size="sm"
                data-font="primary"
                data-pine-script-download=""
                className="shrink-0"
                disabled={!canUseCode}
                onClick={() => downloadText(preview.code, `${filenameBase}.pine`)}
              >
                <span className="normal-case tracking-normal">Download</span>
              </Button>
              <PinePanelHideButton label="Hide Policy Pinescript" onClick={preview.hide} />
            </>
          }
        />
      </div>
    </div>
  );
}
